import { parseApiDate } from "../../utils/datetime";
import React, { useEffect, useMemo, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from "lucide-react";
import FlashAlert from "../../Components/FlashAlert/FlashAlert";
import EventInvitationModal from "../../Components/EventInvitationModal";
import { DeleteIcon, VerifyIcon } from "../../Components/Icons/ActionIcons";
import apiService from "../../services/api.service";
import {
  filterIscrizioni,
  isEmailVerified,
  iscrittiCountLabel,
  selectRegistrationFilters,
} from "../../utils/iscrizioniFilters";
import "./dashboardIscrizioni.css";

const displayName = (user) => {
  if (!user) return "";
  const name = `${user.first_name || ""} ${user.last_name || ""}`.trim();
  return name || user.username;
};

const formatRegistrationDate = (value) => {
  if (!value) return "—";
  const date = parseApiDate(value);
  if (!date) return "—";
  return date.toLocaleString("it-IT", {
    timeZone: "Europe/Rome",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const sortValue = (user, key) => {
  if (key === "first_name") return user.first_name || "";
  if (key === "last_name") return user.last_name || "";
  if (key === "username") return user.username || "";
  if (key === "created_at") return user.created_at ? (parseApiDate(user.created_at)?.getTime() || 0) : 0;
  if (key === "is_verified") return isEmailVerified(user) ? 1 : 0;
  return "";
};

const SortIcon = ({ active, direction }) => {
  if (!active) return <ArrowUpDown aria-hidden="true" />;
  return direction === "asc"
    ? <ArrowUp aria-hidden="true" />
    : <ArrowDown aria-hidden="true" />;
};

const DashboardIscrizioni = () => {
  const [users, setUsers] = useState([]);
  const [selectFields, setSelectFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [userToDelete, setUserToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [fieldFilters, setFieldFilters] = useState({});
  const [verified, setVerified] = useState("");
  const [eventFilter, setEventFilter] = useState("");
  const [showInvitations, setShowInvitations] = useState(false);
  const [verifyingUserId, setVerifyingUserId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sort, setSort] = useState({ key: "created_at", direction: "desc" });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const [usersResponse, appResponse] = await Promise.all([
          apiService.getIscrizioni(),
          apiService.getMyApplication().catch(() => ({ data: null })),
        ]);
        setUsers(usersResponse.data || []);
        const fields = selectRegistrationFilters(appResponse.data?.registration_fields || []);
        setSelectFields(fields);
        setFieldFilters(Object.fromEntries(fields.map((field) => [field.id, ""])));
      } catch {
        setError("Impossibile caricare le iscrizioni.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const registrationEvents = useMemo(() => {
    const events = new Map();
    users.forEach((user) => (user.event_registrations || []).forEach((registration) => {
      events.set(String(registration.event_id), registration.event_name);
    }));
    return [...events].sort((a, b) => a[1].localeCompare(b[1], "it"));
  }, [users]);

  const filteredUsers = useMemo(
    () => filterIscrizioni(users, { fields: fieldFilters, verified }).filter((user) =>
      !eventFilter || (user.event_registrations || []).some((registration) =>
        String(registration.event_id) === eventFilter)
    ),
    [users, fieldFilters, verified, eventFilter]
  );

  const visibleUsers = useMemo(() => {
    const query = searchTerm.trim().toLocaleLowerCase("it");
    const searched = query
      ? filteredUsers.filter((user) =>
          [user.first_name, user.last_name, user.username]
            .filter(Boolean)
            .some((value) => String(value).toLocaleLowerCase("it").includes(query))
        )
      : filteredUsers;
    const collator = new Intl.Collator("it", { sensitivity: "base", numeric: true });
    return [...searched].sort((a, b) => {
      const left = sortValue(a, sort.key);
      const right = sortValue(b, sort.key);
      const comparison = typeof left === "number" && typeof right === "number"
        ? left - right
        : collator.compare(String(left), String(right));
      return sort.direction === "asc" ? comparison : -comparison;
    });
  }, [filteredUsers, searchTerm, sort]);

  const toggleSort = (key) => {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const renderSortableHeader = (label, key) => {
    const active = sort.key === key;
    return (
      <th scope="col" aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}>
        <button
          type="button"
          className={`dashboard-iscrizioni__sort${active ? " is-active" : ""}`}
          onClick={() => toggleSort(key)}
          aria-label={`Ordina per ${label}: ${active && sort.direction === "asc" ? "decrescente" : "crescente"}`}
        >
          <span>{label}</span>
          <SortIcon active={active} direction={sort.direction} />
        </button>
      </th>
    );
  };

  const filterColumns = Math.max(1, selectFields.length + 2);

  const closeDeleteModal = () => {
    if (!deleting) {
      setUserToDelete(null);
      setDeleteError("");
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    setDeleting(true);
    setDeleteError("");
    try {
      await apiService.deleteUser(userToDelete.id);
      setUsers((prev) => prev.filter((user) => user.id !== userToDelete.id));
      setSuccess(`Utente "${displayName(userToDelete)}" eliminato.`);
      setUserToDelete(null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "cannot delete own account") {
        setDeleteError("Non puoi eliminare il tuo account.");
      } else if (detail === "cannot delete this user") {
        setDeleteError("Non puoi eliminare questo utente.");
      } else {
        setDeleteError("Eliminazione non riuscita.");
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleVerify = async (user) => {
    setVerifyingUserId(user.id);
    setError("");
    setSuccess("");
    try {
      const response = await apiService.verifyUserEmail(user.id);
      setUsers((prev) =>
        prev.map((item) =>
          item.id === user.id ? { ...item, ...response.data, is_verified: true } : item
        )
      );
      setSuccess(`Utente "${displayName(user)}" verificato.`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        detail === "cannot verify this user"
          ? "Non puoi verificare questo utente."
          : "Verifica dell'utente non riuscita."
      );
    } finally {
      setVerifyingUserId(null);
    }
  };

  return (
    <div className="dashboard-card">
      <div className="dashboard-card__header">
        <div>
          <h1 className="dashboard-card__title">Iscrizioni</h1>
          <p className="dashboard-card__subtitle">
            Utenti iscritti alla tua applicazione e ai singoli eventi.
          </p>
        </div>
        {!loading && (
          <div className="d-flex align-items-center gap-3 flex-wrap">
            <span className="dashboard-count">{iscrittiCountLabel(visibleUsers.length)}</span>
            <Button className="users-edit-btn" onClick={() => setShowInvitations(true)} disabled={Boolean(error)}>
              Invia mail
            </Button>
          </div>
        )}
      </div>

      {error && <FlashAlert variant="danger" onHidden={() => setError("")}>{error}</FlashAlert>}
      {success && <FlashAlert variant="success" onHidden={() => setSuccess("")}>{success}</FlashAlert>}
      {loading && <p>Caricamento...</p>}
      {showInvitations && <EventInvitationModal users={users} initialEventId={eventFilter}
        onHide={() => setShowInvitations(false)} />}

      {!loading && (
        <div className="dashboard-iscrizioni__toolbar">
          <Form.Group className="dashboard-iscrizioni__search" controlId="iscrizioni-search">
            <Form.Label className="visually-hidden">Cerca iscrizioni</Form.Label>
            <Search aria-hidden="true" />
            <Form.Control
              type="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Cerca per nome, cognome o email"
              aria-label="Cerca iscrizioni"
            />
          </Form.Group>
          <div
            className="dashboard-iscrizioni-filters"
            style={{ gridTemplateColumns: `repeat(${Math.min(filterColumns, 3)}, minmax(180px, 1fr))` }}
          >
            <Form.Group controlId="iscrizioni-evento">
              <Form.Label>Evento</Form.Label>
              <Form.Select value={eventFilter} onChange={(e) => setEventFilter(e.target.value)}
                aria-label="Filtra per evento">
                <option value="">Tutti gli eventi</option>
                {registrationEvents.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </Form.Select>
            </Form.Group>
            {selectFields.map((field) => (
              <Form.Group controlId={`iscrizioni-${field.id}`} key={field.id}>
                <Form.Label>{field.label}</Form.Label>
                <Form.Select
                  value={fieldFilters[field.id] || ""}
                  onChange={(e) =>
                    setFieldFilters((prev) => ({ ...prev, [field.id]: e.target.value }))
                  }
                  aria-label={`Filtra per ${field.label}`}
                >
                  <option value="">Tutti</option>
                  {field.options.map((option) => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            ))}
            <Form.Group controlId="iscrizioni-email">
              <Form.Label>Stato email</Form.Label>
              <Form.Select
                value={verified}
                onChange={(e) => setVerified(e.target.value)}
                aria-label="Filtra per stato email"
              >
                <option value="">Tutte</option>
                <option value="verified">Email verificata</option>
                <option value="unverified">Email non verificata</option>
              </Form.Select>
            </Form.Group>
          </div>
        </div>
      )}

      {!loading && users.length === 0 && !error && (
        <Alert variant="info">Nessun utente iscritto.</Alert>
      )}

      {!loading && users.length > 0 && visibleUsers.length === 0 && (
        <Alert variant="info">Nessun iscritto corrisponde alla ricerca o ai filtri selezionati.</Alert>
      )}

      {!loading && visibleUsers.length > 0 && (
        <div className="dashboard-iscrizioni__table-wrap">
          <table className="dashboard-iscrizioni__table">
            <thead>
              <tr>
                {renderSortableHeader("Nome", "first_name")}
                {renderSortableHeader("Cognome", "last_name")}
                {renderSortableHeader("Email", "username")}
                {renderSortableHeader("Data di iscrizione", "created_at")}
                <th scope="col">Iscrizioni agli eventi</th>
                {renderSortableHeader("Stato email", "is_verified")}
                <th scope="col" className="dashboard-iscrizioni__actions-heading">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {visibleUsers.map((user) => (
                <tr key={user.id}>
                  <td data-label="Nome">{user.first_name || "—"}</td>
                  <td data-label="Cognome">{user.last_name || "—"}</td>
                  <td data-label="Email"><span className="dashboard-iscrizioni__email">{user.username}</span></td>
                  <td data-label="Data di iscrizione">{formatRegistrationDate(user.created_at)}</td>
                  <td data-label="Iscrizioni agli eventi">
                    {(user.event_registrations || []).length ? user.event_registrations.map((registration) => (
                      <div key={registration.event_id} className="mb-1">
                        <span>{registration.event_name}</span>
                        <small className="d-block">{formatRegistrationDate(registration.registered_at)}</small>
                      </div>
                    )) : "Nessuna iscrizione a eventi"}
                  </td>
                  <td data-label="Stato email">
                    <span
                      className={`dashboard-iscrizione-status${
                        isEmailVerified(user) ? " dashboard-iscrizione-status--verified" : ""
                      }`}
                    >
                      {isEmailVerified(user) ? "Email verificata" : "Email non verificata"}
                    </span>
                  </td>
                  <td data-label="Azioni">
                    <div className="dashboard-event-item__actions dashboard-iscrizioni__actions">
                      {!isEmailVerified(user) && (
                        <button
                          type="button"
                          className="dashboard-icon-btn dashboard-icon-btn--verify"
                          title="Verifica utente"
                          aria-label={`Verifica ${displayName(user)}`}
                          disabled={verifyingUserId === user.id}
                          onClick={() => handleVerify(user)}
                        >
                          <VerifyIcon />
                        </button>
                      )}
                      <button
                        type="button"
                        className="dashboard-icon-btn"
                        title="Elimina utente"
                        aria-label={`Elimina ${displayName(user)}`}
                        onClick={() => {
                          setSuccess("");
                          setDeleteError("");
                          setUserToDelete(user);
                        }}
                      >
                        <DeleteIcon />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal show={!!userToDelete} onHide={closeDeleteModal} centered>
        <Modal.Header closeButton={!deleting}>
          <Modal.Title>Elimina utente</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleteError && <FlashAlert variant="danger" onHidden={() => setDeleteError("")}>{deleteError}</FlashAlert>}
          <p>
            Sei sicuro di voler eliminare{" "}
            <strong>{displayName(userToDelete)}</strong>
            {userToDelete?.username ? ` (${userToDelete.username})` : ""}?
          </p>
          <p className="dashboard-delete-warning">
            L&apos;operazione è irreversibile e rimuoverà anche le presenze
            registrate per questo utente.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-primary" onClick={closeDeleteModal} disabled={deleting}>
            Annulla
          </Button>
          <Button className="users-edit-btn" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Eliminazione..." : "Elimina definitivamente"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DashboardIscrizioni;
